import React from "react";

const PrivacyPolicy: React.FC = () => {
  const styles = {
    container: {
      maxWidth: "800px",
      margin: "0 auto",
      padding: "40px",
      fontFamily: "Times New Roman, serif",
      fontSize: "12pt",
      lineHeight: "1.5",
      backgroundColor: "var(--fx-surface-base, #FFFFFF)",
      color: "var(--fx-surface-text, #0F172A)",
    },
    title: {
      textAlign: "center" as const,
      fontSize: "16pt",
      fontWeight: "bold",
      marginBottom: "10px",
    },
    subtitle: {
      textAlign: "center" as const,
      fontSize: "12pt",
      marginBottom: "30px",
    },
    h2: {
      fontSize: "14pt",
      fontWeight: "bold",
      marginTop: "20px",
      marginBottom: "10px",
    },
    h3: {
      fontSize: "12pt",
      fontWeight: "bold",
      marginTop: "15px",
      marginBottom: "8px",
    },
    p: {
      marginBottom: "10px",
      textAlign: "justify" as const,
    },
    ul: {
      marginLeft: "40px",
      marginBottom: "10px",
    },
    li: {
      marginBottom: "5px",
    },
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>FLOWNECT PRIVACY POLICY</h1>
      <p style={styles.subtitle}>
        <strong>Last Updated:</strong> January 26, 2026
      </p>
      <p style={styles.p}>
        Flownect is a software platform owned and operated by EMCB Group. EMCB Group provides business administration, consulting, and operational
        support services through Flownect and related systems. References to “we”, “our”, or “the Company” include EMCB Group and its affiliated
        platforms.
      </p>

      <h2 style={styles.h2}>1. Introduction</h2>
      <p style={styles.p}>
        Flownect ("Flownect," "Company," "we," "us," or "our") is a software-as-a-service (SaaS) provider offering operational, administrative,
        monitoring, and communication tools to businesses in the cleaning and related service industries.
      </p>
      <p style={styles.p}>
        This Privacy Policy applies to all users of the Flownect mobile applications, web applications, dashboards, APIs, and related services
        (collectively, the "Platform").
      </p>
      <p style={styles.p}>
        This Privacy Policy forms part of the Terms and Conditions. Acceptance of the Terms constitutes acceptance of this Privacy Policy.
      </p>

      <h2 style={styles.h2}>2. Scope and Roles</h2>
      <p style={styles.p}>
        Business Users act as data controllers with respect to worker or contractor data processed through the Platform. Flownect acts as a data
        processor when processing such data solely on the instructions of Business Users and for the purpose of providing the Platform.
      </p>
      <p style={styles.p}>Flownect does not determine employment status, worker classification, or labor compliance.</p>

      <h2 style={styles.h2}>3. Information We Collect</h2>

      <h3 style={styles.h3}>3.1 Information Provided by Users</h3>
      <ul style={styles.ul}>
        <li style={styles.li}>Name, email address, phone number</li>
        <li style={styles.li}>Business name and business contact details</li>
        <li style={styles.li}>Profile photographs</li>
        <li style={styles.li}>Uploaded documents (licenses, certifications, insurance, compliance records)</li>
      </ul>
      <p style={styles.p}>Certain information may be optional and provided at the discretion of the user or Business User.</p>

      <h3 style={styles.h3}>3.2 Conditional / Optional Compliance Information</h3>
      <p style={styles.p}>
        The following information is collected only if enabled by a Business User for specific compliance, verification, or administrative workflows
        and is not required for basic Platform usage:
      </p>
      <ul style={styles.ul}>
        <li style={styles.li}>Date of birth</li>
        <li style={styles.li}>Government-issued identifiers (e.g., EIN, SSN, ITIN)</li>
      </ul>

      <h2 style={styles.h2}>4. Location and Device Data</h2>

      <h3 style={styles.h3}>4.1 Types of Location Data</h3>
      <ul style={styles.ul}>
        <li style={styles.li}>Foreground location data</li>
        <li style={styles.li}>Background location data</li>
        <li style={styles.li}>Geofencing data (arrival and departure at job sites)</li>
        <li style={styles.li}>Historical location logs</li>
      </ul>

      <h3 style={styles.h3}>4.2 Purpose of Location Data</h3>
      <ul style={styles.ul}>
        <li style={styles.li}>Verify attendance and presence at assigned job sites</li>
        <li style={styles.li}>Enable time, task, and operational reporting</li>
        <li style={styles.li}>Support Business Users in supervising field operations</li>
      </ul>
      <p style={styles.p}>Location data is not used for advertising or cross-app tracking.</p>

      <h3 style={styles.h3}>4.3 Background Location – Prominent Disclosure</h3>
      <p style={styles.p}>
        Background location tracking occurs only during active work periods, assigned jobs, or logged-in operational sessions, as configured by the
        Business User.
      </p>
      <p style={styles.p}>
        The Platform displays a clear, in-app disclosure explaining background location use before requesting device permissions.
      </p>
      <p style={styles.p}>Users provide consent through:</p>
      <ul style={styles.ul}>
        <li style={styles.li}>Operating system permission prompts</li>
        <li style={styles.li}>In-app notices presented at the time location access is requested</li>
      </ul>
      <p style={styles.p}>Disabling location permissions may limit or disable certain Platform features.</p>

      <h2 style={styles.h2}>5. Communications and Messaging</h2>
      <p style={styles.p}>
        The Platform may facilitate communications between users, including messages, notifications, and system alerts. Communications data is used
        solely to enable Platform functionality.
      </p>
      <p style={styles.p}>
        We may send SMS messages on behalf of EMCB Group for service-related communications, including account updates, scheduling notifications, and
        customer support.
      </p>

      <h2 style={styles.h2}>6. Payments and Financial Data</h2>
      <p style={styles.p}>
        Payments are processed by third-party payment processors (e.g., Stripe, PayPal). Flownect does not store full payment card numbers or banking
        credentials.
      </p>

      <h2 style={styles.h2}>7. Data Use and Processing</h2>
      <ul style={styles.ul}>
        <li style={styles.li}>Operate and improve the Platform</li>
        <li style={styles.li}>Provide requested services and features</li>
        <li style={styles.li}>Maintain security and prevent fraud</li>
        <li style={styles.li}>Comply with applicable legal obligations</li>
      </ul>

      <h2 style={styles.h2}>8. Data Sharing and Disclosure</h2>
      <ul style={styles.ul}>
        <li style={styles.li}>Authorized Business Users and administrators</li>
        <li style={styles.li}>Service providers and processors supporting Platform operations</li>
        <li style={styles.li}>Legal authorities when required by law</li>
      </ul>
      <p style={styles.p}>Flownect does not sell personal data.</p>

      <h2 style={styles.h2}>9. Data Retention</h2>
      <p style={styles.p}>
        Data is retained only for as long as necessary to provide the Platform, comply with legal obligations, resolve disputes, and enforce
        agreements.
      </p>

      <h2 style={styles.h2}>10. Account and Data Deletion</h2>
      <p style={styles.p}>
        Users may initiate account deletion directly within the Platform under Account Settings. Deletion requests may require identity verification
        and confirmation.
      </p>
      <p style={styles.p}>Upon deletion:</p>
      <ul style={styles.ul}>
        <li style={styles.li}>User accounts are permanently deactivated</li>
        <li style={styles.li}>Associated personal data is deleted or anonymized unless retention is required by law</li>
      </ul>
      <p style={styles.p}>Additional information about data deletion is available through Flownect's public deletion request process.</p>

      <h2 style={styles.h2}>11. Children's Privacy</h2>
      <p style={styles.p}>
        The Platform is not directed to children under the age of 13. Flownect does not knowingly collect personal data from children.
      </p>

      <h2 style={styles.h2}>12. Security Measures</h2>
      <p style={styles.p}>Flownect implements administrative, technical, and organizational safeguards designed to protect personal data.</p>

      <h2 style={styles.h2}>13. International Users</h2>
      <p style={styles.p}>
        Users accessing the Platform from outside the United States do so at their own initiative and are responsible for compliance with applicable
        local laws.
      </p>

      <h2 style={styles.h2}>14. Changes to This Policy</h2>
      <p style={styles.p}>
        Flownect may update this Privacy Policy from time to time. Continued use of the Platform constitutes acceptance of the updated policy.
      </p>

      <h2 style={styles.h2}>15. Contact Information</h2>
      <p style={styles.p}>
        <strong>Privacy & Legal Contact:</strong> <a href="mailto:contact@flownect.com">contact@flownect.com</a>
      </p>
      <p style={styles.p}>
        <strong>Support Contact:</strong> <a href="mailto:contact@flownect.com">contact@flownect.com</a>
      </p>
    </div>
  );
};

export default PrivacyPolicy;
